import React, { useEffect, useMemo, useState } from "react";
import { api, fmtEUR, formatApiError, API_BASE } from "../lib/api";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Dialog, DialogContent, DialogTitle } from "../components/ui/dialog";
import {
  Trash2, Plus, FileText, Search, ChevronLeft, ChevronRight, User as UserIcon, CalendarDays, Briefcase,
  Users, Luggage, Pencil, Check, ListOrdered, StickyNote, Eye, Save, X, Calculator, Plane, Info,
} from "lucide-react";
import { toast } from "sonner";

const STATI = ["Bozza", "Inviato", "Accettato", "Rifiutato", "Scaduto"];
const VALIDITY = [7, 15, 30, 60];
const MON = ["GEN","FEB","MAR","APR","MAG","GIU","LUG","AGO","SET","OTT","NOV","DIC"];
const addDays = (iso, n) => { const d = new Date(`${iso}T12:00:00`); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };
const diffDays = (a, b) => Math.round((new Date(`${b}T12:00:00`) - new Date(`${a}T12:00:00`)) / 86400000);
const splitDate = (iso) => { const [y, m, d] = (iso || "").split("-"); return { y, d, mon: m ? MON[Number(m) - 1] : "" }; };

const fieldCls = "bg-black/60 border-amber-500/25 text-[#F5F2E8] focus-visible:ring-amber-500/40";
const GoldCard = ({ icon: Icon, title, right, children, testid }) => (
  <section className="min-w-0 rounded-2xl border border-amber-500/30 bg-gradient-to-b from-[#121009] to-[#070605] overflow-hidden" data-testid={testid}>
    <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-amber-500/15 bg-amber-500/[0.04]">
      <div className="flex items-center gap-2 text-amber-300 font-display text-[15px]"><Icon size={17} className="text-amber-400" />{title}</div>
      {right}
    </div>
    <div className="p-4">{children}</div>
  </section>
);
const GoldBtn = ({ children, className = "", ...p }) => (
  <button type="button" {...p} className={`inline-flex items-center justify-center gap-1.5 rounded-lg font-semibold transition-colors disabled:opacity-50 bg-gradient-to-b from-[#F1D27A] to-[#C99A24] text-black hover:from-[#FFE08A] ${className}`}>{children}</button>
);
const GhostBtn = ({ children, className = "", ...p }) => (
  <button type="button" {...p} className={`inline-flex items-center justify-center gap-1.5 rounded-lg border border-amber-500/40 text-amber-200 hover:bg-amber-500/10 transition-colors disabled:opacity-50 ${className}`}>{children}</button>
);
const Lbl = ({ children }) => <div className="text-[11px] text-[#B8A270] mb-1">{children}</div>;

const DateBadge = ({ iso, time }) => {
  const { y, d, mon } = splitDate(iso);
  return (
    <div className="w-[64px] shrink-0 flex flex-col items-center justify-center border-r border-amber-500/25 py-2 bg-black/40">
      <div className="text-white font-display text-2xl leading-none">{d || "--"}</div>
      <div className="text-amber-300 text-[10px] tracking-widest mt-0.5">{mon}</div>
      <div className="text-slate-500 text-[9px]">{y}</div>
      {time && <div className="text-white text-xs font-mono-num mt-1">{time}</div>}
    </div>
  );
};

function QuoteFields({ s, set }) {
  return (
    <div className="grid grid-cols-2 gap-3 [&>*]:min-w-0">
      <div><Lbl>Data emissione</Lbl><Input type="date" value={s.issueDate} onChange={e => set.issueDate(e.target.value)} className={fieldCls} data-testid="quote-issue-date" /></div>
      <div>
        <Lbl>Validità</Lbl>
        <Select value={String(s.validityDays)} onValueChange={v => set.validityDays(v === "custom" ? "custom" : Number(v))}>
          <SelectTrigger className={fieldCls} data-testid="quote-validity-select"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#0A0907] border-amber-500/30 text-[#F5F2E8]">
            {VALIDITY.map(n => <SelectItem key={n} value={String(n)}>{n} giorni</SelectItem>)}
            <SelectItem value="custom">Data personalizzata</SelectItem>
          </SelectContent>
        </Select>
        {s.validityDays === "custom"
          ? <Input type="date" value={s.validityDate} onChange={e => set.validityDate(e.target.value)} className={`${fieldCls} mt-2`} data-testid="quote-validity-date" />
          : <div className="text-[10px] text-slate-500 mt-1">Scade il {s.validityDate ? s.validityDate.split("-").reverse().join("/") : "—"}</div>}
      </div>
      <div>
        <Lbl>Stato</Lbl>
        <Select value={s.status} onValueChange={set.status}>
          <SelectTrigger className={fieldCls} data-testid="quote-status-select"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#0A0907] border-amber-500/30 text-[#F5F2E8]">{STATI.map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div><Lbl>Aliquota IVA %</Lbl><Input type="number" step="0.5" value={s.vatRate} onChange={e => set.vatRate(Number(e.target.value))} className={fieldCls} data-testid="quote-vat-rate" /></div>
      <label className="col-span-2 flex items-center gap-3 text-xs text-[#E8DFC8] cursor-pointer select-none">
        <button type="button" role="switch" aria-checked={s.pricesIncludeVat} onClick={() => set.pricesIncludeVat(!s.pricesIncludeVat)} data-testid="quote-incl-vat-toggle"
          className={`w-10 h-5 rounded-full relative transition-colors ${s.pricesIncludeVat ? "bg-amber-500" : "bg-slate-700"}`}>
          <span className={`absolute left-0 top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${s.pricesIncludeVat ? "translate-x-5" : "translate-x-0.5"}`} />
        </button>
        I prezzi inseriti sono già IVA inclusa <Info size={13} className="text-amber-400" />
      </label>
    </div>
  );
}

function AvailableServices({ available, onAdd }) {
  const [q, setQ] = useState("");
  const list = available.filter(b => !q || `${b.service_type} ${b.pickup} ${b.destination} ${b.date}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <GoldCard icon={Briefcase} title="Servizi disponibili del cliente" testid="quote-available-services"
      right={<span className="text-xs text-amber-200 border border-amber-500/40 rounded-full px-2 py-0.5">{available.length}</span>}>
      <div className="relative mb-3">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
        <Input placeholder="Cerca servizi…" value={q} onChange={e => setQ(e.target.value)} className={`${fieldCls} pl-9`} data-testid="quote-services-search" />
      </div>
      <div className="space-y-2 max-h-[46vh] overflow-y-auto pr-1">
        {list.length === 0 && <div className="text-xs text-slate-500 text-center py-4">Nessun servizio disponibile</div>}
        {list.map(b => (
          <div key={b.id} className="flex rounded-xl border border-amber-500/20 bg-black/50 overflow-hidden">
            <DateBadge iso={b.date} time={b.time} />
            <div className="flex-1 min-w-0 p-2.5">
              <div className="flex items-start justify-between gap-2">
                <div className="text-white text-sm font-semibold flex items-center gap-1.5 truncate"><Plane size={13} className="text-amber-400 shrink-0" />{b.service_type || "Servizio"}</div>
                <div className="text-amber-300 font-display text-base whitespace-nowrap">{fmtEUR(b.price)}</div>
              </div>
              <div className="text-[11px] text-slate-400 truncate">{b.pickup || "—"} → {b.destination || "—"}</div>
              <div className="flex items-center justify-between mt-1.5">
                <div className="flex gap-3 text-[11px] text-slate-400">
                  <span className="inline-flex items-center gap-1"><Users size={11} className="text-amber-400" />{b.passengers || 1} pax</span>
                  <span className="inline-flex items-center gap-1"><Luggage size={11} className="text-amber-400" />{b.luggage || 0} bagagli</span>
                </div>
                <GoldBtn onClick={() => onAdd(b)} className="px-2.5 py-1 text-xs" data-testid={`add-booking-${b.id}`}><Plus size={12} />Aggiungi</GoldBtn>
              </div>
            </div>
          </div>
        ))}
      </div>
    </GoldCard>
  );
}

function RowCard({ it, i, onChange, onRemove }) {
  const [edit, setEdit] = useState(!it.booking_id && !it.pickup);
  return (
    <div className="rounded-xl border border-amber-500/25 bg-black/50 overflow-hidden" data-testid={`quote-row-card-${i}`}>
      <div className="flex">
        <div className="w-6 flex items-center justify-center text-amber-400 text-xs border-r border-amber-500/15">{i + 1}</div>
        <DateBadge iso={it.date} time={it.time} />
        <div className="flex-1 min-w-0 p-2.5">
          <div className="flex items-start justify-between gap-2">
            <div className="text-white text-sm font-semibold flex items-center gap-1.5 truncate"><Plane size={13} className="text-amber-400 shrink-0" />{it.service_type || "Servizio"}</div>
            <div className="text-amber-300 font-display text-base whitespace-nowrap" data-testid={`quote-row-price-${i}`}>{fmtEUR(it.price)}</div>
          </div>
          <div className="text-[11px] text-slate-300">{it.pickup || "—"} → {it.destination || "—"}</div>
          <div className="flex gap-3 text-[11px] text-slate-400 mt-1">
            <span className="inline-flex items-center gap-1"><Users size={11} className="text-amber-400" />{it.passengers || 1} pax</span>
            <span className="inline-flex items-center gap-1"><Luggage size={11} className="text-amber-400" />{it.luggage || 0} bagagli</span>
          </div>
        </div>
        <div className="flex flex-col justify-center gap-1.5 pr-2">
          <button type="button" onClick={() => setEdit(!edit)} className="p-1.5 rounded-md border border-amber-500/40 text-amber-300" data-testid={`quote-row-edit-${i}`}>{edit ? <Check size={14} /> : <Pencil size={14} />}</button>
          <button type="button" onClick={onRemove} className="p-1.5 rounded-md border border-rose-500/40 text-rose-400" data-testid={`quote-row-delete-${i}`}><Trash2 size={14} /></button>
        </div>
      </div>
      {edit && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 p-3 border-t border-amber-500/15 bg-[#0d0b08]">
          <Input type="date" value={it.date || ""} onChange={e => onChange("date", e.target.value)} className={`${fieldCls} h-9 text-xs`} />
          <Input type="time" value={it.time || ""} onChange={e => onChange("time", e.target.value)} className={`${fieldCls} h-9 text-xs`} />
          <Input placeholder="Servizio" value={it.service_type || ""} onChange={e => onChange("service_type", e.target.value)} className={`${fieldCls} h-9 text-xs`} />
          <Input type="number" step="0.01" placeholder="Prezzo" value={it.price} onChange={e => onChange("price", e.target.value)} className={`${fieldCls} h-9 text-xs text-right`} data-testid={`quote-row-price-input-${i}`} />
          <Input placeholder="Partenza" value={it.pickup || ""} onChange={e => onChange("pickup", e.target.value)} className={`${fieldCls} h-9 text-xs col-span-2`} />
          <Input placeholder="Destinazione" value={it.destination || ""} onChange={e => onChange("destination", e.target.value)} className={`${fieldCls} h-9 text-xs col-span-2`} />
          <Input type="number" min="1" placeholder="Pax" value={it.passengers || 1} onChange={e => onChange("passengers", e.target.value)} className={`${fieldCls} h-9 text-xs`} />
          <Input type="number" min="0" placeholder="Bagagli" value={it.luggage ?? ""} onChange={e => onChange("luggage", e.target.value)} className={`${fieldCls} h-9 text-xs`} />
        </div>
      )}
    </div>
  );
}

function Summary({ totals, vatRate }) {
  return (
    <GoldCard icon={Calculator} title="Riepilogo economico" testid="quote-summary">
      <div className="space-y-1.5 text-sm text-[#E8DFC8]">
        <div className="flex justify-between"><span>Imponibile</span><span className="font-mono-num" data-testid="quote-subtotal">{fmtEUR(totals.subtotal)}</span></div>
        <div className="flex justify-between"><span>IVA {vatRate}%</span><span className="font-mono-num" data-testid="quote-vat-amount">{fmtEUR(totals.vat_amount)}</span></div>
        <div className="flex justify-between items-center rounded-lg border border-amber-400/70 px-3 py-2 mt-2 bg-amber-500/[0.06]">
          <span className="text-amber-200">Totale preventivo</span>
          <span className="text-amber-300 font-display text-2xl" data-testid="quote-total">{fmtEUR(totals.total)}</span>
        </div>
      </div>
    </GoldCard>
  );
}

function PdfPreview({ quote, version }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!quote?.id) return;
    let u; let off = false;
    api.get(`/quotes/${quote.id}/pdf-premium`, { responseType: "blob" }).then(r => {
      if (off) return; u = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" })); setUrl(u);
    }).catch(e => toast.error(formatApiError(e)));
    return () => { off = true; if (u) URL.revokeObjectURL(u); };
  }, [quote?.id, version]);
  if (!quote?.id) return <div className="text-sm text-slate-400 text-center py-10" data-testid="quote-preview-unsaved">Salva il preventivo per vedere l'anteprima del PDF.</div>;
  return (
    <div className="space-y-3" data-testid="quote-pdf-preview">
      {url ? <iframe title="Anteprima PDF" src={url} className="w-full h-[60vh] rounded-xl border border-amber-500/30 bg-white" /> : <div className="text-sm text-slate-400 text-center py-10">Generazione anteprima…</div>}
      <a href={`${API_BASE}/quotes/${quote.id}/pdf-premium`} target="_blank" rel="noreferrer" data-testid="download-quote-pdf"
        className="inline-flex w-full items-center justify-center gap-2 py-2.5 rounded-lg border border-amber-500/40 text-amber-200 hover:bg-amber-500/10"><FileText size={16} />Apri / scarica PDF</a>
    </div>
  );
}

function CustomerCard({ customer, customers, onPick, locked }) {
  const [open, setOpen] = useState(!customer);
  const [q, setQ] = useState("");
  const list = customers.filter(c => !q || (c.name || "").toLowerCase().includes(q.toLowerCase())).slice(0, 50);
  return (
    <GoldCard icon={UserIcon} title="Cliente" testid="quote-customer-card">
      <div className="text-[11px] text-[#B8A270] mb-2">Seleziona il cliente</div>
      <div className="flex gap-2">
        <button type="button" disabled={locked} onClick={() => setOpen(!open)} data-testid="quote-customer-toggle"
          className="flex-1 min-w-0 flex items-center gap-3 rounded-xl border border-amber-500/30 bg-black/50 px-3 py-2.5 text-left">
          <span className="w-9 h-9 rounded-lg bg-gradient-to-b from-[#F1D27A] to-[#C99A24] flex items-center justify-center text-black"><UserIcon size={16} /></span>
          <span className="flex-1 min-w-0">
            <span className="block text-white text-sm font-semibold truncate">{customer?.name || "Nessun cliente selezionato"}</span>
            <span className="block text-[11px] text-slate-400 truncate">{[customer?.email, customer?.phone].filter(Boolean).join(" • ")}</span>
          </span>
          {!locked && <ChevronRight size={16} className="text-amber-400" />}
        </button>
        {!locked && <button type="button" onClick={() => setOpen(true)} className="w-12 rounded-xl border border-amber-500/30 text-amber-300 flex items-center justify-center"><Search size={18} /></button>}
      </div>
      {open && !locked && (
        <div className="mt-3">
          <Input placeholder="Cerca cliente per nome…" value={q} onChange={e => setQ(e.target.value)} className={fieldCls} data-testid="quote-customer-search" />
          <div className="max-h-56 overflow-y-auto mt-2 space-y-1">
            {list.map(c => (
              <button key={c.id} type="button" onClick={() => { onPick(c); setOpen(false); }} data-testid={`pick-customer-${c.id}`}
                className="w-full text-left px-3 py-2 rounded-lg hover:bg-amber-500/10 text-sm text-white flex justify-between gap-2">
                <span className="truncate">{c.name}</span><span className="text-xs text-slate-500 truncate">{c.email || c.phone || ""}</span>
              </button>
            ))}
            {list.length === 0 && <div className="text-xs text-slate-500 text-center py-4">Nessun cliente trovato</div>}
          </div>
        </div>
      )}
    </GoldCard>
  );
}

export default function QuoteModal({ customer: customerProp, onClose, quoteToOpen, customersList }) {
  const [customer, setCustomer] = useState(customerProp || null);
  const [customers, setCustomers] = useState(customersList || []);
  const [bookings, setBookings] = useState([]);
  const [items, setItems] = useState([]);
  const [issueDate, setIssueDate] = useState(new Date().toISOString().slice(0, 10));
  const [validityDays, setValidityDays] = useState(15);
  const [validityDate, setValidityDate] = useState("");
  const [vatRate, setVatRate] = useState(10);
  const [pricesIncludeVat, setPricesIncludeVat] = useState(false);
  const [status, setStatus] = useState("Bozza");
  const [notes, setNotes] = useState("");
  const [savedQuote, setSavedQuote] = useState(null);
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState(quoteToOpen ? "edit" : "create");
  const [tab, setTab] = useState("righe");
  const [showAdd, setShowAdd] = useState(false);
  const [pdfVersion, setPdfVersion] = useState(0);

  useEffect(() => {
    if (customerProp || customers.length > 0) return;
    api.get("/customers").then(r => setCustomers(r.data || [])).catch(() => {});
  }, [customerProp]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (customerProp || !quoteToOpen?.customer_id || customer) return;
    api.get(`/customers/${quoteToOpen.customer_id}`).then(r => setCustomer(r.data)).catch(() => {});
  }, [quoteToOpen, customerProp]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!customer) return;
    Promise.all([api.get("/bookings", { params: { customer_id: customer.id } }), api.get("/settings/company")]).then(([b, s]) => {
      setBookings((b.data || []).filter(x => x.status !== "Cancellata"));
      if (!quoteToOpen) setVatRate(s.data?.vat_rate ?? 10);
    }).catch(() => {});
  }, [customer?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!quoteToOpen) return;
    setItems(quoteToOpen.items || []);
    const iss = quoteToOpen.issue_date || new Date().toISOString().slice(0, 10);
    setIssueDate(iss);
    const vd = quoteToOpen.validity_date || "";
    setValidityDate(vd);
    const dd = vd ? diffDays(iss, vd) : null;
    setValidityDays(dd && VALIDITY.includes(dd) ? dd : (vd ? "custom" : 15));
    setVatRate(quoteToOpen.vat_rate ?? 10);
    setPricesIncludeVat(!!quoteToOpen.prices_include_vat);
    setStatus(quoteToOpen.status || "Bozza");
    setNotes(quoteToOpen.notes || "");
    setSavedQuote(quoteToOpen);
  }, [quoteToOpen]);

  useEffect(() => {
    if (validityDays !== "custom" && issueDate) setValidityDate(addDays(issueDate, Number(validityDays)));
  }, [issueDate, validityDays]);

  const totals = useMemo(() => {
    const line = items.reduce((s, it) => s + Number(it.price || 0), 0);
    let subtotal, vat_amount, total;
    if (pricesIncludeVat) { total = line; subtotal = total / (1 + vatRate / 100); vat_amount = total - subtotal; }
    else { subtotal = line; vat_amount = subtotal * (vatRate / 100); total = subtotal + vat_amount; }
    return { subtotal: Number(subtotal.toFixed(2)), vat_amount: Number(vat_amount.toFixed(2)), total: Number(total.toFixed(2)) };
  }, [items, vatRate, pricesIncludeVat]);

  const addBooking = (b) => {
    if (items.find(x => x.booking_id === b.id)) { toast.info("Servizio già aggiunto"); return; }
    setItems(prev => [...prev, { booking_id: b.id, date: b.date, time: b.time, service_type: b.service_type, pickup: b.pickup,
      destination: b.destination, passengers: b.passengers, luggage: b.luggage ?? null, price: Number(b.price || 0) }]);
    toast.success("Servizio aggiunto al preventivo");
  };
  const addBlank = () => setItems(prev => [...prev, { booking_id: null, date: issueDate, time: "", service_type: "", pickup: "", destination: "", passengers: 1, luggage: null, price: 0 }]);
  const removeItem = (i) => setItems(prev => prev.filter((_, idx) => idx !== i));
  const setItem = (i, k, v) => setItems(prev => prev.map((it, idx) => idx === i ? { ...it, [k]: v } : it));

  const save = async () => {
    if (!customer) { toast.error("Seleziona un cliente"); return null; }
    if (items.length === 0) { toast.error("Aggiungi almeno un servizio"); return null; }
    setSaving(true);
    try {
      const payload = {
        customer_id: customer.id, issue_date: issueDate, validity_date: validityDate || null,
        status, notes, vat_rate: vatRate, prices_include_vat: pricesIncludeVat,
        items: items.map(it => ({ ...it, price: Number(it.price || 0), passengers: Number(it.passengers || 1),
          luggage: it.luggage === "" || it.luggage == null ? null : Number(it.luggage) })),
      };
      const r = savedQuote ? await api.patch(`/quotes/${savedQuote.id}`, payload) : await api.post("/quotes", payload);
      setSavedQuote(r.data); setPdfVersion(v => v + 1);
      toast.success(`Preventivo ${r.data?.quote_number || ""} salvato`);
      return r.data;
    } catch (e) { toast.error(formatApiError(e)); return null; }
    finally { setSaving(false); }
  };
  const openPreview = async () => { const q = await save(); if (q) { setView("edit"); setTab("anteprima"); } };

  const available = bookings.filter(b => !items.find(i => i.booking_id === b.id));
  const s = { issueDate, validityDays, validityDate, status, vatRate, pricesIncludeVat };
  const set = { issueDate: setIssueDate, validityDays: setValidityDays, validityDate: setValidityDate, status: setStatus, vatRate: setVatRate, pricesIncludeVat: setPricesIncludeVat };
  const TABS = [["righe", "Righe", ListOrdered], ["dettagli", "Dettagli", Briefcase], ["note", "Note", StickyNote], ["anteprima", "Anteprima", Eye]];

  return (
    <Dialog open onOpenChange={(o) => !o && onClose(!!savedQuote)}>
      <DialogContent className="max-w-3xl w-full h-[100dvh] sm:h-auto sm:max-h-[94vh] overflow-y-auto overflow-x-hidden p-0 gap-0 block rounded-none sm:rounded-2xl bg-[#060504] border-amber-500/30 text-[#F5F2E8] [&>button]:hidden" data-testid="quote-modal">
        <div className="sticky top-0 z-10 flex items-center gap-3 px-4 py-3 border-b border-amber-500/20 bg-[#060504]/95 backdrop-blur">
          <button type="button" onClick={() => view === "edit" && !quoteToOpen ? setView("create") : onClose(!!savedQuote)} data-testid="quote-back-btn"
            className="w-9 h-9 rounded-full border border-amber-500/40 flex items-center justify-center text-amber-300"><ChevronLeft size={18} /></button>
          <div className="flex-1 min-w-0">
            <DialogTitle className="font-display text-xl text-white truncate">
              {view === "create" ? "Nuovo preventivo" : `Preventivo · ${customer?.name || ""}`}
            </DialogTitle>
            <div className="text-xs text-[#B8A270] truncate">{view === "create" ? "Crea un preventivo per il tuo cliente" : (savedQuote?.quote_number || "Non ancora salvato")}</div>
          </div>
          {view === "edit" && <span className="text-xs border border-amber-500/40 rounded-lg px-2.5 py-1 text-amber-200" data-testid="quote-status-badge">{status}</span>}
        </div>

        {view === "edit" && (
          <div className="grid grid-cols-4 border-b border-amber-500/15">
            {TABS.map(([k, label, Icon]) => (
              <button key={k} type="button" onClick={() => setTab(k)} data-testid={`quote-tab-${k}`}
                className={`flex flex-col items-center gap-1 py-2.5 text-xs transition-colors ${tab === k ? "bg-gradient-to-b from-amber-500/25 to-amber-500/5 text-amber-200 border-b-2 border-amber-400" : "text-slate-400"}`}>
                <Icon size={17} />{label}
              </button>
            ))}
          </div>
        )}

        <div className="p-4 space-y-4">
          {view === "create" && (
            <>
              <CustomerCard customer={customer} customers={customers} onPick={c => { setCustomer(c); setItems([]); }} locked={!!customerProp} />
              {customer && <GoldCard icon={CalendarDays} title="Dati preventivo" testid="quote-data-card"><QuoteFields s={s} set={set} /></GoldCard>}
              {customer && <AvailableServices available={available} onAdd={addBooking} />}
              {customer && (
                <GoldBtn onClick={() => setView("edit")} className="w-full py-3 text-base" data-testid="quote-continue-btn">
                  Continua · {items.length} {items.length === 1 ? "riga" : "righe"} <ChevronRight size={18} />
                </GoldBtn>
              )}
            </>
          )}

          {view === "edit" && tab === "righe" && (
            <GoldCard icon={ListOrdered} title="Righe preventivo" testid="quote-rows-card"
              right={<GhostBtn onClick={() => setShowAdd(!showAdd)} className="px-2.5 py-1 text-xs" data-testid="quote-add-row-btn"><Plus size={13} />Aggiungi riga</GhostBtn>}>
              {showAdd && (
                <div className="mb-3 space-y-3">
                  <GhostBtn onClick={() => { addBlank(); setShowAdd(false); }} className="w-full py-2 text-sm" data-testid="quote-add-blank-row"><Plus size={14} />Riga vuota (servizio personalizzato)</GhostBtn>
                  <AvailableServices available={available} onAdd={addBooking} />
                </div>
              )}
              <div className="space-y-2.5">
                {items.length === 0 && <div className="text-sm text-slate-500 text-center py-6">Nessuna riga. Usa "Aggiungi riga".</div>}
                {items.map((it, i) => <RowCard key={`${it.booking_id || "r"}-${i}`} it={it} i={i} onChange={(k, v) => setItem(i, k, v)} onRemove={() => removeItem(i)} />)}
              </div>
            </GoldCard>
          )}

          {view === "edit" && tab === "dettagli" && (
            <>
              <CustomerCard customer={customer} customers={customers} onPick={setCustomer} locked />
              <GoldCard icon={CalendarDays} title="Dati preventivo" testid="quote-data-card"><QuoteFields s={s} set={set} /></GoldCard>
              {customer && (customer.address || customer.vat) && (
                <div className="text-xs text-slate-400 px-1">{[customer.address, customer.vat && `P.IVA ${customer.vat}`].filter(Boolean).join(" · ")}</div>
              )}
            </>
          )}

          {view === "edit" && tab === "note" && (
            <GoldCard icon={StickyNote} title="Note preventivo" testid="quote-notes-card">
              <Textarea rows={6} maxLength={1000} value={notes} onChange={e => setNotes(e.target.value)} className={fieldCls} data-testid="quote-notes" />
              <div className="text-right text-[11px] text-slate-500 mt-1">{notes.length}/1000</div>
            </GoldCard>
          )}

          {view === "edit" && tab === "anteprima" && <PdfPreview quote={savedQuote} version={pdfVersion} />}

          {view === "edit" && (
            <>
              <Summary totals={totals} vatRate={vatRate} />
              <GoldBtn onClick={save} disabled={saving} className="w-full py-3 text-base" data-testid="save-quote-btn">
                <Save size={18} />{saving ? "Salvataggio…" : (savedQuote ? "Aggiorna preventivo" : "Salva preventivo")}
              </GoldBtn>
              <div className="grid grid-cols-2 gap-2 pb-[env(safe-area-inset-bottom)]">
                <GhostBtn onClick={openPreview} disabled={saving} className="py-2.5 text-sm" data-testid="quote-preview-btn"><Eye size={16} />Anteprima PDF</GhostBtn>
                <GhostBtn onClick={() => onClose(!!savedQuote)} className="py-2.5 text-sm" data-testid="quote-close-btn"><X size={16} />Chiudi</GhostBtn>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
